import { useState, useEffect } from 'react';
import { collection, getDocs, doc, getDoc, onSnapshot, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase/config';

// Hook for fetching a collection
export const useCollection = (collectionName) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const querySnapshot = await getDocs(collection(db, collectionName));
        const documents = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setData(documents);
        setError(null);
      } catch (err) {
        setError(err.message);
        console.error('Error fetching collection:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [collectionName]);

  return { data, loading, error };
};

// Hook for fetching a single document
export const useDocument = (collectionName, documentId) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!documentId) {
      setLoading(false);
      return;
    }

    const fetchDocument = async () => {
      try {
        setLoading(true);
        const docRef = doc(db, collectionName, documentId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          setData({ id: docSnap.id, ...docSnap.data() });
        } else {
          setData(null);
        }
        setError(null);
      } catch (err) {
        setError(err.message);
        console.error('Error fetching document:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDocument();
  }, [collectionName, documentId]);

  return { data, loading, error };
};

// Hook for real-time collection updates
export const useRealtimeCollection = (collectionName) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, collectionName),
      (querySnapshot) => {
        const documents = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setData(documents);
        setLoading(false);
        setError(null);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
        console.error('Error with realtime updates:', err);
      }
    );

    return () => unsubscribe();
  }, [collectionName]);

  return { data, loading, error };
};

// Hook for fetching featured laptops from nested collection
export const useFeaturedLaptops = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchFeaturedLaptops = async () => {
      try {
        setLoading(true);
        // This path implies a specific structure: products -> laptops -> items
        const querySnapshot = await getDocs(collection(db, 'products', 'laptops', 'items'));
        const laptops = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Filter for featured laptops
        const featuredLaptops = laptops.filter(laptop => laptop.featured === true);
        setData(featuredLaptops);
        setError(null);
      } catch (err) {
        setError(err.message);
        console.error('Error fetching featured laptops:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedLaptops();
  }, []);

  return { data, loading, error };
};

// NEW HOOK: Hook for fetching documents with custom queries (where, orderBy, limit)
export const useCollectionQuery = (collectionName, queryOptions = {}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        let q = collection(db, collectionName);

        // Apply where clause if options are provided
        if (queryOptions.field && queryOptions.operator && queryOptions.value !== undefined) {
          q = query(q, where(queryOptions.field, queryOptions.operator, queryOptions.value));
        }
        // Add other query options like orderBy and limit if needed in the future
        if (queryOptions.orderByField && queryOptions.orderByDirection) {
            q = query(q, orderBy(queryOptions.orderByField, queryOptions.orderByDirection));
        }
        if (queryOptions.limit) {
            q = query(q, limit(queryOptions.limit));
        }


        const querySnapshot = await getDocs(q);
        const documents = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setData(documents);
        setError(null);
      } catch (err) {
        setError(err.message);
        console.error(`Error fetching collection '${collectionName}' with query:`, queryOptions, err);
      } finally {
        setLoading(false);
      }
    };

    // Using JSON.stringify for queryOptions to ensure it's a dependency for useEffect
    // This correctly re-runs the effect if query options change.
    fetchData();
  }, [collectionName, JSON.stringify(queryOptions)]);

  return { data, loading, error };
};

// NEW HOOK: Hook for fetching trending products from multiple collections
export const useTrendingProducts = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTrendingProducts = async () => {
      try {
        setLoading(true);
        const trendingProducts = [];
        // Fetch from laptops collection
        const laptopsRef = collection(db, 'products', 'laptops', 'items');
        const laptopsSnapshot = await getDocs(laptopsRef);
        const laptopsData = laptopsSnapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data(),
            productType: 'laptop'
          }))
          .filter(item => item.trending === true || item.trending === "true");
        
        trendingProducts.push(...laptopsData);

        // Fetch from minipc collection
        const minipcRef = collection(db, 'products', 'minipc', 'items');
        const minipcSnapshot = await getDocs(minipcRef);
        const minipcData = minipcSnapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data(),
            productType: 'minipc'
          }))
          .filter(item => item.trending === true || item.trending === "true");
        
        trendingProducts.push(...minipcData);

        // Shuffle the combined results for variety
        const shuffled = trendingProducts.sort(() => Math.random() - 0.5);

        setData(shuffled);
        setError(null);
      } catch (err) {
        setError(err.message);
        console.error('Error fetching trending products:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTrendingProducts();
  }, []);

  return { data, loading, error };
};
